import React, { useState, useEffect, useRef } from 'react';
import './index.less';

// 全局缓存，缓存所有已加载的SVG帧
const globalFrameCache: Record<string, string> = {};

interface FramePlayerProps {
    basePath: string;    // 基础路径
    fileName: string;    // 文件名模板 (例如: 'Oren_*.svg')
    frameCount: number;  // 总帧数
    fps: number;         // 每秒帧数
    topColor?: string;   // 顶部颜色
    bottomColor?: string; // 底部颜色
    startFrame?: number; // 起始帧号
    hash?: string;       // 唯一标识符，用于生成唯一的类名
    zoomScale?: number;  // 放大倍率
    hasOcImage?: boolean; // 是否是OC图片
}

// 将颜色加深10%
const darkenColor = (color: string): string => {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  const darkerR = Math.max(0, Math.floor(r * 0.9));
  const darkerG = Math.max(0, Math.floor(g * 0.9));
  const darkerB = Math.max(0, Math.floor(b * 0.9));
  
  return `#${darkerR.toString(16).padStart(2, '0')}${darkerG.toString(16).padStart(2, '0')}${darkerB.toString(16).padStart(2, '0')}`;
};

// 生成随机hash
const generateHash = () => {
  return Date.now().toString(36) + Math.random().toString(36).substring(2, 8);
};

const FramePlayer: React.FC<FramePlayerProps> = ({ 
    basePath, 
    fileName, 
    frameCount, 
    fps,
    topColor,
    bottomColor,
    startFrame = 0,
    hash,
    zoomScale = 1.00,
    hasOcImage = false
}) => {
    const [currentFrame, setCurrentFrame] = useState(startFrame);
    const [svgContent, setSvgContent] = useState<string>('');
    const animationFrameRef = useRef<number>();
    const lastFrameTimeRef = useRef<number>(0);
    const componentHash = useRef<string>(hash || generateHash());
    const svgClass = `frame-player-svg-${componentHash.current}`;
    const isLoadingRef = useRef<boolean>(false);
    const loadingProgressRef = useRef<number>(0);
    
    // 获取帧的路径
    const getFramePath = (frameNumber: number) => {
        if (hasOcImage) {
            // 对于OC图片，直接使用basePath作为hash值
            const hash = basePath.replace(/[\/\.]/g, ''); // 清理路径字符
            return `https://oss.usecubes.cn/incredibox/oc/aianimation/${hash}-${frameNumber}.png`;
        }
        return `https://oss.usecubes.cn/incredibox/assets/files/${basePath}${fileName.replace('*', frameNumber.toString())}`;
    };

    // 构建全局缓存的key
    const getCacheKey = (frameNumber: number) => {
        if (hasOcImage) {
            const hash = basePath.replace(/[\/\.]/g, ''); // 清理路径字符
            return `aianimation/${hash}-${frameNumber}.png`;
        }
        return `${basePath}/${fileName.replace('*', frameNumber.toString())}`;
    };

    // 检测文件格式
    const getFileExtension = (frameNumber: number) => {
        if (hasOcImage) {
            return 'png';
        }
        const path = fileName.replace('*', frameNumber.toString());
        const extension = path.split('.').pop()?.toLowerCase();
        return extension || 'svg';
    };

    // 创建内联样式
    const createInlineStyle = () => {
        if (!topColor && !bottomColor) return '';

        // console.log('topColor', topColor);
        // console.log('bottomColor', bottomColor);
        
        const topColorValue = topColor || '#ffffff';
        const bottomColorValue = bottomColor || '#ffffff';
        const topShadowColor = darkenColor(topColorValue);
        const bottomShadowColor = darkenColor(bottomColorValue);
        const uniqueId = componentHash.current;
        
        return `
            .${svgClass} #head_${uniqueId} {
                fill: ${topColorValue} !important;
            }
            .${svgClass} #head_shadow_${uniqueId} {
                fill: ${topShadowColor} !important;
            }
            .${svgClass} #body_${uniqueId} {
                fill: ${bottomColorValue} !important;
            }
            .${svgClass} #body_shadow_${uniqueId} {
                fill: ${bottomShadowColor} !important;
            }
        `;
    };

    // 加载单个帧（支持SVG和PNG等格式）
    const loadFrame = async (frameNumber: number): Promise<string> => {
        const cacheKey = getCacheKey(frameNumber);
        
        // 如果全局缓存中有该帧，则直接使用
        if (globalFrameCache[cacheKey]) {
            let svgContent = globalFrameCache[cacheKey];
            
            // 如果是SVG内容，需要为当前组件重新设置类名和ID
            if (svgContent.includes('<svg')) {
                // 为当前组件添加类名
                if (!svgContent.includes(`class="${svgClass}"`)) {
                    svgContent = svgContent.replace(/<svg/, `<svg class="${svgClass}"`);
                }
                
                // 为当前组件生成唯一的ID，避免ID冲突
                const uniqueId = componentHash.current;
                svgContent = svgContent.replace(/id="head"/g, `id="head_${uniqueId}"`);
                svgContent = svgContent.replace(/id="head_shadow"/g, `id="head_shadow_${uniqueId}"`);
                svgContent = svgContent.replace(/id="body"/g, `id="body_${uniqueId}"`);
                svgContent = svgContent.replace(/id="body_shadow"/g, `id="body_shadow_${uniqueId}"`);
            }
            
            return svgContent;
        }
        
        try {
            const path = getFramePath(frameNumber);
            const fileExtension = getFileExtension(frameNumber);
            
            if (fileExtension === 'svg') {
                // 处理SVG文件
            const response = await fetch(path);
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const svgText = await response.text();
            
            // 提取SVG内容，移除HTML标签
            const svgMatch = svgText.match(/<svg[^>]*>[\s\S]*<\/svg>/i);
            if (svgMatch) {
                let svgContent = svgMatch[0];
                
                // 保存原始SVG到全局缓存（不包含组件特定的修改）
                globalFrameCache[cacheKey] = svgContent;
                
                // 为当前组件添加类名
                svgContent = svgContent.replace(/<svg/, `<svg class="${svgClass}"`);
                
                // 为当前组件生成唯一的ID，避免ID冲突
                const uniqueId = componentHash.current;
                svgContent = svgContent.replace(/id="head"/g, `id="head_${uniqueId}"`);
                svgContent = svgContent.replace(/id="head_shadow"/g, `id="head_shadow_${uniqueId}"`);
                svgContent = svgContent.replace(/id="body"/g, `id="body_${uniqueId}"`);
                svgContent = svgContent.replace(/id="body_shadow"/g, `id="body_shadow_${uniqueId}"`);
                
                return svgContent;
            } else {
                    throw new Error('No SVG content found');
                }
            } else {
                // 处理PNG/JPG等图片文件
                // 预加载图片以确保缓存
                const img = new Image();
                await new Promise((resolve, reject) => {
                    img.onload = resolve;
                    img.onerror = reject;
                    img.src = path;
                });
                
                // 创建img标签内容
                const imgContent = `<img src="${path}" alt="animation frame" style="width:100%;height:100%;object-fit:contain;" />`;
                
                // 保存到全局缓存
                globalFrameCache[cacheKey] = imgContent;
                
                return imgContent;
            }
        } catch (error) {
            console.error(`Error loading frame ${frameNumber}:`, error);
            // 在错误情况下，尝试使用img标签
            const path = getFramePath(frameNumber);
            const imgContent = `<img src="${path}" alt="animation frame" style="width:100%;height:100%;object-fit:contain;" />`;
            return imgContent;
        }
    };

    // 预加载所有帧
    const preloadFrames = async () => {
        if (isLoadingRef.current) return;
        isLoadingRef.current = true;
        
        try {
            // 首先加载当前帧
            const currentFrameSvg = await loadFrame(currentFrame);
            setSvgContent(currentFrameSvg);
            
            // 然后加载其他帧
            const totalFrames = frameCount;
            let loadedFrames = 1; // 当前帧已加载
            
            for (let i = 0; i < totalFrames; i++) {
                if (i === currentFrame) continue; // 跳过当前帧，因为已经加载
                
                const cacheKey = getCacheKey(i);
                if (!globalFrameCache[cacheKey]) {
                    await loadFrame(i);
                }
                
                loadedFrames++;
                loadingProgressRef.current = (loadedFrames / totalFrames) * 100;
            }
            
            console.log(`All ${totalFrames} frames preloaded successfully`);
        } catch (error) {
            console.error('Error preloading frames:', error);
        } finally {
            isLoadingRef.current = false;
        }
    };

    // 初始化时预加载所有帧
    useEffect(() => {
        // 仅在组件首次加载和props变化时预加载
        preloadFrames();
    }, [basePath, fileName]);

    // 监听帧数和起始帧变化
    useEffect(() => {
        setCurrentFrame(startFrame);
        lastFrameTimeRef.current = 0;
    }, [frameCount, startFrame]);

    // 更新当前帧
    useEffect(() => {
        // 加载并应用当前帧，从全局缓存或网络获取
        loadFrame(currentFrame).then(svgContent => {
            setSvgContent(svgContent);
        });
    }, [currentFrame, topColor, bottomColor]);

    // 监听颜色变化，强制更新样式
    useEffect(() => {
        if (svgContent && (topColor || bottomColor)) {
            // 当颜色变化时，重新处理SVG内容
            let updatedSvgContent = svgContent;
            if (updatedSvgContent.includes('<svg')) {
                // 确保类名正确应用
                if (!updatedSvgContent.includes(`class="${svgClass}"`)) {
                    updatedSvgContent = updatedSvgContent.replace(/<svg/, `<svg class="${svgClass}"`);
                }
                
                // 确保ID正确设置
                const uniqueId = componentHash.current;
                if (!updatedSvgContent.includes(`id="head_${uniqueId}"`)) {
                    updatedSvgContent = updatedSvgContent.replace(/id="head"/g, `id="head_${uniqueId}"`);
                    updatedSvgContent = updatedSvgContent.replace(/id="head_shadow"/g, `id="head_shadow_${uniqueId}"`);
                    updatedSvgContent = updatedSvgContent.replace(/id="body"/g, `id="body_${uniqueId}"`);
                    updatedSvgContent = updatedSvgContent.replace(/id="body_shadow"/g, `id="body_shadow_${uniqueId}"`);
                }
                
                setSvgContent(updatedSvgContent);
            }
        }
    }, [topColor, bottomColor]);

    // 动画循环
    useEffect(() => {
        // 清除之前的动画
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
        }
        
        const animate = (timestamp: number) => {
            if (!lastFrameTimeRef.current) {
                lastFrameTimeRef.current = timestamp;
            }
            
            const elapsed = timestamp - lastFrameTimeRef.current;
            const frameInterval = 1000 / fps;
            
            if (elapsed >= frameInterval) {
                setCurrentFrame(prev => {
                    // 确保帧号在有效范围内
                    const nextFrame = (prev + 1) % frameCount;
                    return nextFrame < startFrame ? startFrame : nextFrame;
                });
                lastFrameTimeRef.current = timestamp;
            }
            
            animationFrameRef.current = requestAnimationFrame(animate);
        };
        
        animationFrameRef.current = requestAnimationFrame(animate);
        
        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, [fps, frameCount, startFrame]);

    return (
        <div className="frame-player" style={{ transform: `scale(${zoomScale})` }}>
            <style>{createInlineStyle()}</style>
            {svgContent ? (
                <div 
                    className="svg-container"
                    dangerouslySetInnerHTML={{ __html: svgContent }}
                />
            ) : (
                <div className="loading">
                    载入中... {Math.round(loadingProgressRef.current)}%
                </div>
            )}
        </div>
    );
};

export default FramePlayer; 